import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectManageSubcalendars, selectModalSubmitting } from '../selectors';
import { closeConnectModal, updateSubcalendar } from '../ducks';
import { selectHouseholdUsers, getUserColor } from '@/features/household/selectors';

const COLOR_PALETTE = [
  '', // empty = "use owner color"
  '#FF6B6B',
  '#4ECDC4',
  '#FFD166',
  '#6A4C93',
  '#118AB2',
  '#F4A261',
  '#06D6A0',
  '#9B5DE5',
  '#000000',
];

/**
 * Manage screen for a connection's subcalendars. Shows one row per
 * subcalendar with an enable toggle, a color swatch picker (empty
 * swatch = "use the assigned owner's palette color"), and a member
 * dropdown that routes events to a specific household member's
 * family-view bucket.
 */
export function ManageSubcalendars(): JSX.Element {
  const dispatch = useDispatch();
  const subcalendars = useSelector(selectManageSubcalendars);
  const submitting = useSelector(selectModalSubmitting);
  const users = useSelector(selectHouseholdUsers);

  const sorted = React.useMemo(
    () => [...subcalendars].sort((a, b) => a.display_name.localeCompare(b.display_name)),
    [subcalendars],
  );

  if (submitting && subcalendars.length === 0) {
    return <div className="modal__body"><p>Loading…</p></div>;
  }

  return (
    <div className="modal__body manage-subcalendars">
      <p className="modal__hint">
        Pick which calendars to sync, give each a color, and (optionally)
        assign a calendar to a specific household member so its events
        show under them in family view.
      </p>
      {sorted.length === 0 ? (
        <p className="modal__picker-empty">No subcalendars discovered yet — try Sync now.</p>
      ) : (
        <ul className="manage-subcalendars__list">
          {sorted.map((sc) => {
            const ownerForColor = sc.assigned_owner_id || '';
            const fallbackColor = ownerForColor ? getUserColor(users, ownerForColor) : null;
            return (
              <li key={sc.id} className="manage-subcalendars__row">
                <label className="manage-subcalendars__toggle">
                  <input
                    type="checkbox"
                    checked={sc.enabled}
                    onChange={(e) =>
                      dispatch(updateSubcalendar(sc.id, { enabled: e.target.checked }))
                    }
                  />
                  <span className="manage-subcalendars__name">{sc.display_name || sc.external_id}</span>
                </label>

                <div className="manage-subcalendars__colors" role="radiogroup" aria-label="Color">
                  {COLOR_PALETTE.map((c) => {
                    const isSelected = (sc.color || '') === c;
                    const swatchColor = c || fallbackColor || 'transparent';
                    return (
                      <button
                        key={c || 'default'}
                        type="button"
                        role="radio"
                        aria-checked={isSelected}
                        title={c || 'Use owner color'}
                        className={`manage-subcalendars__swatch${isSelected ? ' manage-subcalendars__swatch--selected' : ''}${c ? '' : ' manage-subcalendars__swatch--default'}`}
                        style={{ background: swatchColor }}
                        onClick={() => dispatch(updateSubcalendar(sc.id, { color: c }))}
                      />
                    );
                  })}
                </div>

                <select
                  className="manage-subcalendars__owner"
                  value={sc.assigned_owner_id || ''}
                  onChange={(e) =>
                    dispatch(updateSubcalendar(sc.id, { assignedOwnerId: e.target.value }))
                  }
                  aria-label="Assigned member"
                >
                  <option value="">Default (you)</option>
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.display_name || u.email}
                    </option>
                  ))}
                </select>
              </li>
            );
          })}
        </ul>
      )}
      <div className="modal__actions">
        <button
          type="button"
          className="settings-section__btn settings-section__btn--primary"
          onClick={() => dispatch(closeConnectModal())}
        >
          Done
        </button>
      </div>
    </div>
  );
}

export default ManageSubcalendars;
